import type { PageNode, SectionNode, SectionOf, SectionType, SiteInfo, WebsiteDefinition } from "@baze/schema";
import type { ReactNode } from "react";
import type { NavLink } from "./nav";
import type { EngineTheme } from "./theme";

export type RenderMode = "live" | "editor";

/** Everything a section component may read. Plain data: safe to build on the server. */
export interface RenderContext {
  definition: WebsiteDefinition;
  site: SiteInfo;
  page: PageNode;
  theme: EngineTheme;
  mode: RenderMode;
  editor: boolean;
  nav: NavLink[];
  /** Section whose title renders as the page's single <h1>. */
  h1SectionId: string | null;
  /** Section whose first image loads eagerly with fetchpriority="high" (LCP). */
  priorityImageSectionId: string | null;
  /** Anchors that exist on this page (visible sections), for in-page link fallbacks. */
  anchors: ReadonlySet<string>;
  siteId?: string;
  selectedSectionId?: string | null;
  hoveredSectionId?: string | null;
  /** Prefix for DOM ids, so several renders (thumbnails) can share one document. */
  idPrefix: string;
  /** Non-interactive render (thumbnails): no eager images. */
  thumbnail: boolean;
  year: number;
  /** Render time (ms since epoch) for time-relative content such as countdowns. */
  now: number;
}

export interface SectionProps<T extends SectionType = SectionType> {
  node: SectionOf<T>;
  ctx: RenderContext;
}

export type SectionComponent<T extends SectionType = SectionType> = (props: SectionProps<T>) => ReactNode;

export type AnySection = SectionNode;

/** `data-field` attributes for editor double-click → inspector focus. Empty in live mode. */
export function field(ctx: RenderContext, path: string): { "data-field"?: string } {
  return ctx.editor ? { "data-field": path } : {};
}

export function domId(ctx: RenderContext, id: string): string {
  return `${ctx.idPrefix}${id}`;
}

export function titleId(ctx: RenderContext, node: { id: string }): string {
  return domId(ctx, `${node.id}-title`);
}

export function messageVars(ctx: RenderContext, produk?: string) {
  return { nama_usaha: ctx.site.name, ...(produk ? { produk } : {}) };
}
